import { Tooltip } from '@nextui-org/react';
import DOMPurifyFactory from 'dompurify';
import { marked } from 'marked';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EnterIcon from '~/assets/EnterIcon';
import PlusIcon from '~/assets/PlusIcon';
import SendIcon from '~/assets/SendIcon';
import { ConversationEntityType } from '~/components/AIInsightsModal/AIInsightsModal.utils';
import {
  createAssistantMessage,
  createErrorMessage,
  createUserMessage,
  MESSAGES_PER_PAGE,
  removeLastErrorMessage,
  SCROLL_THRESHOLD,
} from '~/components/AIInsightsModal/AIInsightsModal.utils';
import {
  EntryPoint,
  SuggestedAction,
  Trigger,
  TriggerPayload,
  useBedrockStream,
} from '~/components/AIInsightsModal/useBedrockStream';
import { useTypewriter } from '~/components/AIInsightsModal/useTypewriter';
import { TooltipContent } from '~/components/HelpTooltip/HelpTooltip.styles';
import { visitIQAiInsightsSidebarWidth } from '~/constants/localStorage';
import { useWindowDimensions } from '~/hooks/useDimensions';
import { ConversationMessage, ConversationRole, ConversationSection } from '~/types/api.graphql';
import { useGetConversationMessagesQuery } from '~/types/api.graphql';

import {
  AddAudienceButton,
  ButtonWrapper,
  ChatContainer,
  CloseButton,
  EmptyStateContainer,
  EmptyStateSubtitle,
  EmptyStateTitle,
  InputActionsRow,
  InputContainer,
  InputWrapper,
  LoadingContainer,
  LoadingDot,
  LoadingDotsContainer,
  LoadingMoreContainer,
  LoadingSubtitle,
  LoadingTitle,
  MarkdownContent,
  MessageBubble,
  MessageContainer,
  MessagesList,
  PrivacyDisclaimer,
  ResizeHandle,
  SendButton,
  SidebarHeader,
  SidebarInner,
  SidebarTitleContainer,
  SidebarTitleText,
  SidebarWrapper,
  StyledBottomSheet,
  StyledTextarea,
  SuggestedActionButton,
  SuggestedActionIcon,
  SuggestedActionLabel,
  SuggestedActionsContainer,
  TextareaWrapper,
  ToolStatusContainer,
  ToolStatusDot,
  ToolStatusText,
  UserMessageBubble,
} from './AIInsightsSidebar.styles';
import { MAX_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH, useAIInsightsSidebar } from './AIInsightsSidebarContext';

// Explicitly create instance with current window to avoid issues when the
// module-level `createDOMPurify()` runs before DOM is fully ready
const DOMPurify = DOMPurifyFactory(window);

const SuggestedActionItem = ({ label, onClick }: { label: string; onClick: () => void }) => {
  const labelRef = useRef<HTMLSpanElement>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    const el = labelRef.current;
    if (el) setIsTruncated(el.scrollWidth > el.offsetWidth);
  }, []);

  const button = (
    <SuggestedActionButton type="button" onClick={onClick} onMouseEnter={checkTruncation}>
      <SuggestedActionIcon>
        <EnterIcon />
      </SuggestedActionIcon>
      <SuggestedActionLabel ref={labelRef}>{label}</SuggestedActionLabel>
    </SuggestedActionButton>
  );

  if (!isTruncated) return button;

  return (
    <Tooltip content={<TooltipContent>{label}</TooltipContent>} placement="top" portalClassName="tooltip-wrapper-big">
      {button}
    </Tooltip>
  );
};

const FADE_DURATION = 300;

const AIInsightsSidebar = () => {
  const { t } = useTranslation();
  const { isInMobile } = useWindowDimensions();
  // Context is always present here since AIInsightsSidebar is rendered inside the provider
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const ctx = useAIInsightsSidebar()!;
  const {
    isOpen,
    section,
    entityId,
    entityType,
    closeSidebar,
    sidebarWidth,
    setSidebarWidth,
    isResizing,
    setIsResizing,
  } = ctx;

  // ---------------------------------------------------------------------------
  // Resize handler
  // ---------------------------------------------------------------------------
  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      const startX: number = e.clientX;
      const startWidth: number = sidebarWidth;
      setIsResizing(true);

      const onMouseMove = (ev: MouseEvent) => {
        const delta: number = startX - ev.clientX; // drag left → wider
        setSidebarWidth(Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, startWidth + delta)));
      };

      const onMouseUp = (ev: MouseEvent) => {
        const finalWidth: number = Math.max(
          MIN_SIDEBAR_WIDTH,
          Math.min(MAX_SIDEBAR_WIDTH, startWidth + (startX - ev.clientX)),
        );
        localStorage.setItem(visitIQAiInsightsSidebarWidth, `${finalWidth}`);
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [sidebarWidth, setSidebarWidth, setIsResizing],
  );

  // ---------------------------------------------------------------------------
  // Chat state
  // ---------------------------------------------------------------------------
  const [messages, setMessages] = useState<ConversationMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [suggestedActions, setSuggestedActions] = useState<SuggestedAction[]>([]);
  const [toolStatus, setToolStatus] = useState<string | null>(null);
  const [toolStatusVisible, setToolStatusVisible] = useState(false);

  // ---------------------------------------------------------------------------
  // Refs
  // ---------------------------------------------------------------------------
  const messagesListRef = useRef<HTMLDivElement>(null);
  const streamingMessageRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const streamingBufferRef = useRef<string>('');
  const displayedLengthRef = useRef<number>(0);
  const isInitialLoadRef = useRef(true);
  const isLoadingMoreRef = useRef(false);
  const scrollHeightBeforeLoadRef = useRef(0);
  const isSidebarOpenRef = useRef(false);
  const userScrolledUpRef = useRef(false);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasToolStatusRef = useRef(false);
  const ctaOpenSentRef = useRef(false);
  const prevSectionRef = useRef<ConversationSection | null>(null);
  const prevEntityIdRef = useRef<string | null>(null);

  // ---------------------------------------------------------------------------
  // Query
  // ---------------------------------------------------------------------------
  const {
    data,
    loading: isLoadingMessages,
    fetchMore,
  } = useGetConversationMessagesQuery({
    variables: {
      section,
      identifier: entityId,
      first: MESSAGES_PER_PAGE,
    },
    skip: !isOpen || !entityId,
    fetchPolicy: 'network-only',
  });

  const hasMoreMessages = data?.getConversationMessages?.pageInfo?.hasNextPage ?? false;
  const endCursor = data?.getConversationMessages?.pageInfo?.endCursor;
  const agentSessionId = data?.getConversationMessages?.agentSessionId;

  // ---------------------------------------------------------------------------
  // Scroll helpers
  // ---------------------------------------------------------------------------
  const scrollToBottom = useCallback((force = false) => {
    if (messagesListRef.current && (force || !userScrolledUpRef.current)) {
      messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
    }
  }, []);

  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (e.deltaY < 0) {
      userScrolledUpRef.current = true;
    } else if (e.deltaY > 0 && messagesListRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = messagesListRef.current;
      if (scrollHeight - scrollTop - clientHeight < 50) {
        userScrolledUpRef.current = false;
      }
    }
  }, []);

  const maintainScrollPosition = useCallback((previousScrollHeight: number) => {
    setTimeout(() => {
      if (messagesListRef.current) {
        const newScrollHeight = messagesListRef.current.scrollHeight;
        messagesListRef.current.scrollTop = newScrollHeight - previousScrollHeight;
      }
      isLoadingMoreRef.current = false;
    }, 0);
  }, []);

  // ---------------------------------------------------------------------------
  // Tool status helpers
  // ---------------------------------------------------------------------------
  const clearFadeTimer = useCallback(() => {
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  }, []);

  const handleToolStatus = useCallback(
    (status: string | null) => {
      clearFadeTimer();
      if (status === null) {
        hasToolStatusRef.current = false;
        setToolStatusVisible(false);
        fadeTimerRef.current = setTimeout(() => {
          setToolStatus(null);
        }, FADE_DURATION);
        return;
      }

      if (hasToolStatusRef.current) {
        setToolStatusVisible(false);
        fadeTimerRef.current = setTimeout(() => {
          setToolStatus(status);
          requestAnimationFrame(() => {
            setToolStatusVisible(true);
          });
        }, FADE_DURATION);
      } else {
        hasToolStatusRef.current = true;
        setToolStatus(status);
        requestAnimationFrame(() => {
          setToolStatusVisible(true);
        });
      }
    },
    [clearFadeTimer],
  );

  const streamingRefCallback = useCallback((node: HTMLDivElement | null) => {
    streamingMessageRef.current = node;
    if (node && streamingBufferRef.current.length > 0) {
      node.innerHTML = DOMPurify.sanitize(marked.parse(streamingBufferRef.current) as string);
      displayedLengthRef.current = streamingBufferRef.current.length;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // Hooks
  // ---------------------------------------------------------------------------
  const { startTypewriter, stopTypewriter, waitForTypewriterCompletion } = useTypewriter({
    streamingBufferRef,
    displayedLengthRef,
    streamingMessageRef,
    scrollToBottom,
  });

  const entryPoint: EntryPoint = useMemo(
    () => ({
      section: section ?? ConversationSection.PixelAudienceOverviewSegments,
      entityId: entityId ?? '',
      entityType: entityType ?? ConversationEntityType.Audience,
      ctaSource: section ?? ConversationSection.PixelAudienceOverviewSegments,
    }),
    [section, entityId, entityType],
  );

  const { sendMessage: sendBedrockMessage } = useBedrockStream({
    sessionIdRef,
    abortControllerRef,
    streamingBufferRef,
    entryPoint,
    onToolStatus: handleToolStatus,
  });

  // ---------------------------------------------------------------------------
  // Reset helpers
  // ---------------------------------------------------------------------------
  const resetChatState = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    stopTypewriter();
    clearFadeTimer();
    setIsSending(false);
    setIsStreaming(false);
    setToolStatus(null);
    setToolStatusVisible(false);
    hasToolStatusRef.current = false;
    streamingBufferRef.current = '';
    displayedLengthRef.current = 0;
    setInputValue('');
    setMessages([]);
    setSuggestedActions([]);
    sessionIdRef.current = null;
    isInitialLoadRef.current = true;
    ctaOpenSentRef.current = false;
    userScrolledUpRef.current = false;
  }, [stopTypewriter, clearFadeTimer]);

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------

  // Reset when sidebar closes
  useEffect(() => {
    if (isOpen) {
      isSidebarOpenRef.current = true;
    } else {
      isSidebarOpenRef.current = false;
      resetChatState();
    }
  }, [isOpen, resetChatState]);

  // Reset when section/entityId change (different button clicked while open)
  useEffect(() => {
    if (!isOpen) return;
    const sectionChanged = section !== prevSectionRef.current;
    const entityChanged = entityId !== prevEntityIdRef.current;

    if ((sectionChanged || entityChanged) && (prevSectionRef.current !== null || prevEntityIdRef.current !== null)) {
      resetChatState();
    }

    prevSectionRef.current = section;
    prevEntityIdRef.current = entityId;
  }, [isOpen, section, entityId, resetChatState]);

  useEffect(() => {
    if (!isOpen || !data?.getConversationMessages?.nodes) return;

    const apiMessages = data.getConversationMessages.nodes as ConversationMessage[];
    const chronologicalMessages = [...apiMessages].reverse();
    const wasLoadingMore = isLoadingMoreRef.current;
    const previousScrollHeight = scrollHeightBeforeLoadRef.current;

    setMessages(chronologicalMessages);

    if (isInitialLoadRef.current) {
      isInitialLoadRef.current = false;
      setTimeout(() => scrollToBottom(true), 50);
    } else if (wasLoadingMore) {
      maintainScrollPosition(previousScrollHeight);
    }
  }, [isOpen, data, scrollToBottom, maintainScrollPosition]);

  useEffect(() => {
    if (agentSessionId) {
      sessionIdRef.current = agentSessionId;
    }
  }, [agentSessionId]);

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleLoadMore = useCallback(async () => {
    if (!hasMoreMessages || isLoadingMessages || isLoadingMore || !endCursor) return;

    setIsLoadingMore(true);
    isLoadingMoreRef.current = true;
    scrollHeightBeforeLoadRef.current = messagesListRef.current?.scrollHeight ?? 0;

    try {
      await fetchMore({
        variables: { after: endCursor },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getConversationMessages) return prev;
          return {
            ...prev,
            getConversationMessages: {
              ...fetchMoreResult.getConversationMessages,
              nodes: [
                ...(prev.getConversationMessages.nodes ?? []),
                ...(fetchMoreResult.getConversationMessages.nodes ?? []),
              ],
            },
          };
        },
      });
    } catch {
      isLoadingMoreRef.current = false;
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMoreMessages, isLoadingMessages, isLoadingMore, endCursor, fetchMore]);

  const handleScroll = useCallback(() => {
    if (!messagesListRef.current || isLoadingMore) return;
    if (messagesListRef.current.scrollTop < SCROLL_THRESHOLD && hasMoreMessages) {
      void handleLoadMore();
    }
  }, [hasMoreMessages, handleLoadMore, isLoadingMore]);

  const startStream = useCallback(
    async (payload: TriggerPayload, displayText?: string) => {
      if (isSending || isStreaming || !sessionIdRef.current) return;

      setInputValue('');
      setIsSending(true);
      setIsStreaming(true);
      setSuggestedActions([]);
      setToolStatus(null);
      setToolStatusVisible(false);
      hasToolStatusRef.current = false;
      streamingBufferRef.current = '';
      displayedLengthRef.current = 0;
      userScrolledUpRef.current = false;
      startTypewriter();

      if (displayText) {
        setMessages((prev) => [...removeLastErrorMessage(prev), createUserMessage(displayText)]);
      }
      setTimeout(() => scrollToBottom(true), 0);

      abortControllerRef.current = new AbortController();

      try {
        setIsSending(false);
        const result = await sendBedrockMessage(payload);

        if (streamingBufferRef.current.length > 0) {
          await waitForTypewriterCompletion();
        }

        if (result.text) {
          setMessages((prev) => [...prev, createAssistantMessage(result.text)]);
        }
        if (result.actions.length > 0) {
          setSuggestedActions(result.actions);
          requestAnimationFrame(() => {
            setTimeout(() => scrollToBottom(true), 100);
          });
        }
      } catch (error: unknown) {
        if ((error as Error).name === 'AbortError') return;
        setMessages((prev) => [...prev, createErrorMessage(t('aiInsights.errorSendingMessage'))]);
      } finally {
        stopTypewriter();
        clearFadeTimer();
        setIsSending(false);
        setIsStreaming(false);
        setToolStatus(null);
        setToolStatusVisible(false);
        streamingBufferRef.current = '';
        displayedLengthRef.current = 0;
        abortControllerRef.current = null;
        setTimeout(() => textareaRef.current?.focus(), 0);
      }
    },
    [
      isSending,
      isStreaming,
      t,
      startTypewriter,
      stopTypewriter,
      sendBedrockMessage,
      waitForTypewriterCompletion,
      scrollToBottom,
      clearFadeTimer,
    ],
  );

  // Send cta_open when sidebar opens and session is ready
  useEffect(() => {
    if (isOpen && agentSessionId && !ctaOpenSentRef.current) {
      ctaOpenSentRef.current = true;
      void startStream(Trigger.CtaOpen);
    }
  }, [isOpen, agentSessionId, startStream]);

  const handleSendMessage = useCallback(async () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    await startStream({ prompt: trimmed }, trimmed);
  }, [inputValue, startStream]);

  const handleActionClick = useCallback(
    async (action: SuggestedAction) => {
      const label: string = action.reason ?? action.title;
      await startStream({ prompt: label }, label);
    },
    [startStream],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void handleSendMessage();
      }
    },
    [handleSendMessage],
  );

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target as HTMLTextAreaElement;
    const lineHeight: number = parseFloat(getComputedStyle(textarea).lineHeight);
    const maxHeight: number = lineHeight * 5;
    const clampedHeight: number = Math.min(Math.max(textarea.scrollHeight, 32), maxHeight);
    textarea.style.height = `${clampedHeight}px`;
    textarea.scrollTop = textarea.scrollHeight;
  }, []);

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const isInitialLoading = isLoadingMessages || !agentSessionId;
  const isEmpty = !isLoadingMessages && agentSessionId && messages.length === 0 && !isStreaming;

  const renderLoadingState = () => (
    <LoadingContainer>
      <LoadingTitle>{t('aiInsights.loadingMessages')}</LoadingTitle>
      <LoadingSubtitle>{t('aiInsights.loadingMessagesSubtitle')}</LoadingSubtitle>
      <LoadingDotsContainer>
        {[...Array(6)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingContainer>
  );

  const renderEmptyState = () => (
    <EmptyStateContainer>
      <EmptyStateTitle>{t('aiInsights.emptyStateTitle')}</EmptyStateTitle>
      <EmptyStateSubtitle>{t('aiInsights.emptyStateSubtitle')}</EmptyStateSubtitle>
    </EmptyStateContainer>
  );

  const renderLoadingMore = () => (
    <LoadingMoreContainer>
      <LoadingDotsContainer>
        {[...Array(3)].map((_, i) => (
          <LoadingDot key={i} $delay={i * 0.2} />
        ))}
      </LoadingDotsContainer>
    </LoadingMoreContainer>
  );

  const renderMessages = () => (
    <>
      {isLoadingMore && renderLoadingMore()}
      {messages.map((message: ConversationMessage) => (
        <React.Fragment key={message.id}>
          <MessageContainer $isUser={message.role === ConversationRole.User}>
            {message.role === ConversationRole.User ? (
              <UserMessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(message.body ?? '') as string) }}
                />
              </UserMessageBubble>
            ) : (
              <MessageBubble>
                <MarkdownContent
                  dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(marked.parse(message.body ?? '') as string) }}
                />
              </MessageBubble>
            )}
          </MessageContainer>
        </React.Fragment>
      ))}
      {isStreaming && (
        <MessageContainer>
          <MessageBubble>
            {toolStatus ? (
              <ToolStatusContainer>
                <ToolStatusDot />
                <ToolStatusText $visible={toolStatusVisible}>{toolStatus}</ToolStatusText>
              </ToolStatusContainer>
            ) : (
              <MarkdownContent ref={streamingRefCallback} style={{ minHeight: '1em' }}>
                <ToolStatusContainer>
                  <ToolStatusDot />
                  <ToolStatusText $visible>{t('aiInsights.thinking')}</ToolStatusText>
                </ToolStatusContainer>
              </MarkdownContent>
            )}
          </MessageBubble>
        </MessageContainer>
      )}
    </>
  );

  const shouldShowSuggestedActions = !isStreaming && suggestedActions.length > 0;

  const renderContent = () => {
    if (isInitialLoading) return renderLoadingState();
    if (isEmpty) return renderEmptyState();
    return renderMessages();
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const renderInner = (fixedWidth = false) => (
    <SidebarInner $width={fixedWidth ? sidebarWidth : 0}>
      <SidebarHeader>
        <SidebarTitleContainer>
          <SidebarTitleText>{t('aiInsights.modalTitle')}</SidebarTitleText>
        </SidebarTitleContainer>
        <CloseButton onClick={closeSidebar} aria-label={t('common.close')}>
          ✕
        </CloseButton>
      </SidebarHeader>
      <ChatContainer>
        <MessagesList ref={messagesListRef} onScroll={handleScroll} onWheel={handleWheel}>
          {renderContent()}
        </MessagesList>
        {shouldShowSuggestedActions && (
          <SuggestedActionsContainer>
            {suggestedActions.map((action) => {
              const label = action.reason ?? action.title;
              return (
                <SuggestedActionItem key={action.key} label={label} onClick={() => void handleActionClick(action)} />
              );
            })}
          </SuggestedActionsContainer>
        )}
        <InputContainer>
          <InputWrapper>
            <TextareaWrapper>
              <StyledTextarea
                ref={textareaRef}
                value={inputValue}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
                placeholder={t('aiInsights.inputPlaceholder')}
                disabled={isSending || !agentSessionId}
                rows={1}
              />
            </TextareaWrapper>
            <InputActionsRow>
              <AddAudienceButton type="button" disabled>
                <PlusIcon />
                {t('aiInsights.addAudience')}
              </AddAudienceButton>
              <ButtonWrapper>
                <SendButton
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isSending || isStreaming || !agentSessionId}
                  type="button"
                >
                  <SendIcon />
                </SendButton>
              </ButtonWrapper>
            </InputActionsRow>
          </InputWrapper>
        </InputContainer>
        <PrivacyDisclaimer>{t('aiInsights.privacyDisclaimer')}</PrivacyDisclaimer>
      </ChatContainer>
    </SidebarInner>
  );

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (isInMobile) {
    return (
      <StyledBottomSheet open={isOpen} onDismiss={closeSidebar} scrollLocking={false}>
        {renderInner()}
      </StyledBottomSheet>
    );
  }

  return (
    <SidebarWrapper
      $isOpen={isOpen}
      $width={sidebarWidth}
      $isResizing={isResizing}
      onWheel={(e) => e.stopPropagation()}
    >
      <ResizeHandle onMouseDown={handleResizeMouseDown} />
      {renderInner(true)}
    </SidebarWrapper>
  );
};

export default AIInsightsSidebar;
